import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

interface DiscardUserDrawerModalProps {
    onKeepEditing: () => void;
    onDiscard: () => void;
}

// Sibling to practice/communications/DiscardChangesConfirmModal - same overlay/card/
// focus/Escape conventions, reused for the user drawer's dirty-close guard.
export function DiscardUserDrawerModal({ onKeepEditing, onDiscard }: DiscardUserDrawerModalProps) {
    const keepEditingRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        keepEditingRef.current?.focus();
    }, []);

    useEscapeKey(onKeepEditing, true);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="discard-user-modal-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
            <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">
                <button
                    onClick={onKeepEditing}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>

                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3
                            id="discard-user-modal-title"
                            className="text-lg font-semibold text-gray-900"
                        >
                            Discard changes?
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            This user has unsaved changes. If you close now they will be lost.
                        </p>
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        ref={keepEditingRef}
                        onClick={onKeepEditing}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Keep editing
                    </button>
                    <button
                        onClick={onDiscard}
                        className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Discard changes
                    </button>
                </div>
            </div>
        </div>
    );
}
